import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getFacebookTracking } from '@/hooks/useFacebookTracking';

declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: unknown;
  }
}

// Generate unique event ID for deduplication between pixel and CAPI
const generateEventId = () => {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
};

// Get UTM params from sessionStorage for custom_data
const getUtmCustomData = (): Record<string, string> => {
  try {
    const stored = sessionStorage.getItem('utm_params');
    if (stored) {
      const params = JSON.parse(stored);
      const customData: Record<string, string> = {};
      if (params.utm_source) customData.utm_source = params.utm_source;
      if (params.utm_medium) customData.utm_medium = params.utm_medium;
      if (params.utm_campaign) customData.utm_campaign = params.utm_campaign;
      if (params.utm_term) customData.utm_term = params.utm_term;
      if (params.utm_content) customData.utm_content = params.utm_content;
      if (params.gclid) customData.gclid = params.gclid;
      if (params.fbclid) customData.fbclid = params.fbclid;
      if (params.msclkid) customData.msclkid = params.msclkid;
      if (params.landing_page) customData.landing_page = params.landing_page;
      if (params.referrer) customData.referrer = params.referrer;
      return customData;
    }
  } catch (e) {
    console.warn('Failed to read UTM params for CAPI:', e);
  }
  return {};
};

interface TrackEventParams {
  eventName: string;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  clientIpAddress?: string;
  eventSourceUrl?: string;
  customData?: Record<string, unknown>;
}

export const useFacebookPixel = () => {
  /**
   * Core dual-tracking function: fires both client-side pixel AND server-side CAPI
   * with the SAME event_id for deduplication.
   */
  const trackEvent = useCallback(async ({
    eventName,
    email,
    phone,
    firstName,
    lastName,
    clientIpAddress,
    eventSourceUrl,
    customData = {},
  }: TrackEventParams) => {
    const eventId = generateEventId();
    const { fbc, fbp } = getFacebookTracking();
    const userAgent = navigator.userAgent;
    const sourceUrl = eventSourceUrl || window.location.href;

    // Merge UTM params into custom_data for CAPI
    const utmData = getUtmCustomData();
    const mergedCustomData = { ...utmData, ...customData };

    console.log(`[FB Dual Track] ${eventName}`, { eventId, fbc: !!fbc, fbp: !!fbp });

    // 1. Client-side pixel event with Advanced Matching user data
    if (typeof window.fbq === 'function') {
      // Build Advanced Matching object for this event
      const advancedMatching: Record<string, string> = {};
      if (email) advancedMatching.em = email.toLowerCase().trim();
      if (phone) advancedMatching.ph = phone.replace(/\D/g, '');
      if (firstName) advancedMatching.fn = firstName.toLowerCase().trim();
      if (lastName) advancedMatching.ln = lastName.toLowerCase().trim();

      // If we have user data, update pixel with Advanced Matching
      if (Object.keys(advancedMatching).length > 0) {
        window.fbq('init', '915167174494073', advancedMatching);
      }

      window.fbq('track', eventName, customData, { eventID: eventId });
      console.log(`[FB Pixel] ${eventName} fired (client-side)`, { eventId });
    }

    // 2. Server-side Conversion API event
    try {
      const { data, error } = await supabase.functions.invoke('fb-conversion', {
        body: {
          event_name: eventName,
          event_id: eventId,
          email,
          phone,
          first_name: firstName,
          last_name: lastName,
          client_ip_address: clientIpAddress,
          client_user_agent: userAgent,
          fbc,
          fbp,
          event_source_url: sourceUrl,
          custom_data: Object.keys(mergedCustomData).length > 0 ? mergedCustomData : undefined,
        },
      });

      if (error) {
        console.error('[FB CAPI] Error:', error);
      } else {
        console.log(`[FB CAPI] ${eventName} sent (server-side)`, data);
      }
    } catch (err) {
      console.error('[FB CAPI] Failed to send event:', err);
    }
  }, []);

  // Track PageView with dual pixel + CAPI
  const trackPageView = useCallback((clientIpAddress?: string) => {
    return trackEvent({
      eventName: 'PageView',
      clientIpAddress,
    });
  }, [trackEvent]);

  // Track ViewContent event (landing page view)
  const trackViewContent = useCallback((params: Omit<TrackEventParams, 'eventName'> & { contentName?: string; contentCategory?: string }) => {
    const { contentName, contentCategory, ...rest } = params;
    const customData: Record<string, unknown> = { ...(rest.customData || {}) };
    if (contentName) customData.content_name = contentName;
    if (contentCategory) customData.content_category = contentCategory;
    return trackEvent({ ...rest, eventName: 'ViewContent', customData });
  }, [trackEvent]);

  // Track Lead event on form submission
  const trackLead = useCallback((params: Omit<TrackEventParams, 'eventName'>) => {
    return trackEvent({ ...params, eventName: 'Lead' });
  }, [trackEvent]);

  // Track CompleteRegistration event
  const trackCompleteRegistration = useCallback((params: Omit<TrackEventParams, 'eventName'>) => {
    return trackEvent({ ...params, eventName: 'CompleteRegistration' });
  }, [trackEvent]);

  // Track Schedule event (custom)
  const trackSchedule = useCallback((params: Omit<TrackEventParams, 'eventName'>) => {
    return trackEvent({ ...params, eventName: 'Schedule' });
  }, [trackEvent]);

  return {
    trackEvent,
    trackPageView,
    trackViewContent,
    trackLead,
    trackCompleteRegistration,
    trackSchedule,
  };
};

export default useFacebookPixel;
